/**
 * Shared Skio selling plan helpers.
 *
 * Single source of truth for two questions every buy box needs to answer the
 * same way: which selling plan does this variant actually subscribe to, and
 * what does that plan cost. Keeping it here is what stops the badge, the
 * subscribe price, the $/serving line and the hidden selling_plan input from
 * drifting apart from each other or from Skio.
 *
 * Prices come from variant.selling_plan_allocations[].price, which Shopify
 * computes from the live Skio plan (percentage, fixed price or per-variant
 * override alike). Local math is only a fallback for when the variant carries
 * no allocation for the plan.
 */
(function () {
  const normalize = (value) =>
    String(value === null || value === undefined ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  window.CtrSkioPlans = {
    /**
     * Group ids, group names or plan ids hidden from standard PDPs, from the
     * theme setting. Accepts a comma separated mix of the three.
     */
    excludedTokens() {
      return String(window.skioExcludedGroupId || '')
        .split(',')
        .map(normalize)
        .filter(Boolean);
    },

    isSkioGroup(group) {
      return String((group && group.app_id) || '')
        .toUpperCase()
        .includes('SKIO');
    },

    /**
     * Group ids are hashes of the group's own properties, so they change when
     * the group is reconfigured. Matching the name too keeps the exclusion
     * working after a rename in Skio.
     */
    isExcludedGroup(group) {
      if (!group) return false;
      const groupId = normalize(group.id);
      const groupName = normalize(group.name);
      return this.excludedTokens().some(
        (token) => token === groupId || (groupName && groupName.includes(token))
      );
    },

    isExcludedPlan(plan) {
      if (!plan) return false;
      const planId = normalize(plan.id);
      return this.excludedTokens().some((token) => token === planId);
    },

    normalizePlan(plan, group) {
      const adjustments = plan.price_adjustments || [];
      const adjustment = adjustments[0] || {};
      return {
        id: plan.id,
        name: plan.name,
        group_id: group ? group.id : null,
        group_name: group ? group.name : null,
        discount: adjustment.value || 0,
        discount_type: adjustment.value_type || '',
        // For fixed price plans the adjustment value IS the price, not a delta
        fixed_price: adjustment.value_type === 'price' ? adjustment.value : null,
        // Kept whole: a first-order promo is a second tier in here
        adjustments,
      };
    },

    /** The Skio groups a standard PDP may offer. */
    skioGroups(product) {
      const skioGroups = ((product && product.selling_plan_groups) || []).filter((group) =>
        this.isSkioGroup(group)
      );
      const allowed = skioGroups.filter((group) => !this.isExcludedGroup(group));

      // Rather than leave a PDP with no subscribe option at all, fall back to
      // the excluded groups when they are the only ones the product has.
      if (!allowed.length && skioGroups.length) {
        console.warn(
          'ctr-skio-plan-utils: every Skio group on this product is excluded, falling back to them',
          skioGroups.map((group) => `${group.name} (${group.id})`)
        );
      }
      return allowed.length ? allowed : skioGroups;
    },

    /**
     * Every Skio plan the PDP may offer, across all non-excluded groups.
     * Reading all of them is what makes a plan reassignment in Skio show up
     * without a code change.
     */
    collectPlans(product) {
      const source = this.skioGroups(product);
      const plans = [];
      const seen = {};
      source.forEach((group) => {
        (group.selling_plans || []).forEach((plan) => {
          if (!plan || this.isExcludedPlan(plan)) return;
          const key = String(plan.id);
          if (seen[key]) return;
          seen[key] = true;
          plans.push(this.normalizePlan(plan, group));
        });
      });
      return plans;
    },

    allocationsFor(variant) {
      return (variant && variant.selling_plan_allocations) || [];
    },

    allocationPlanId(allocation) {
      if (!allocation) return null;
      return (
        allocation.selling_plan_id ||
        (allocation.selling_plan && allocation.selling_plan.id) ||
        null
      );
    },

    getAllocation(variant, planId) {
      if (planId === null || planId === undefined) return null;
      return (
        this.allocationsFor(variant).find(
          (allocation) => String(this.allocationPlanId(allocation)) === String(planId)
        ) || null
      );
    },

    /** Shopify-computed subscribe price in cents, or null when not allocated. */
    getAllocationPrice(variant, planId) {
      const allocation = this.getAllocation(variant, planId);
      return allocation && typeof allocation.price === 'number' ? allocation.price : null;
    },

    /**
     * The plan this variant is really allocated to, in the order Shopify lists
     * the allocations. Never picks by bare index, which is how a variant used
     * to end up subscribed to another group's plan.
     */
    pickPlanForVariant(variant, plans) {
      const list = plans || [];
      if (!list.length) return null;

      const byId = {};
      list.forEach((plan) => {
        byId[String(plan.id)] = plan;
      });

      const allocated = this.allocationsFor(variant)
        .map((allocation) => byId[String(this.allocationPlanId(allocation))])
        .filter(Boolean)[0];

      return allocated || list[0];
    },

    /** Price of one unit on this plan, in cents. */
    unitSubscribePrice(variant, plan) {
      if (!variant || typeof variant.price !== 'number') return null;
      if (!plan) return variant.price;

      const allocationPrice = this.getAllocationPrice(variant, plan.id);
      if (allocationPrice !== null) return allocationPrice;

      if (plan.discount_type === 'price' && typeof plan.fixed_price === 'number') {
        return plan.fixed_price;
      }
      if (plan.discount_type === 'percentage' && plan.discount) {
        return Math.round(variant.price - (variant.price * plan.discount) / 100);
      }
      if (plan.discount_type === 'fixed_amount' && plan.discount) {
        return Math.max(0, variant.price - plan.discount);
      }
      return variant.price;
    },

    /** What the discount is measured against: compare-at when it is a real one. */
    basePrice(variant) {
      if (!variant || typeof variant.price !== 'number') return null;
      const compareAt = variant.compare_at_price;
      return typeof compareAt === 'number' && compareAt > variant.price ? compareAt : variant.price;
    },

    discountPercentage(variant, plan) {
      const base = this.basePrice(variant);
      const subscribePrice = this.unitSubscribePrice(variant, plan);
      if (!base || subscribePrice === null || subscribePrice >= base) return 0;
      return Math.round(((base - subscribePrice) / base) * 100);
    },

    /**
     * What renewals are charged at. When a promo gives the first order its own
     * price adjustment tier, the last tier is the ongoing rate.
     */
    renewalPercentage(variant, plan) {
      const adjustments = (plan && plan.adjustments) || [];
      const last = adjustments[adjustments.length - 1];
      if (!last) return this.discountPercentage(variant, plan);

      if (last.value_type === 'percentage') return Math.round(last.value || 0);

      const base = this.basePrice(variant);
      if (!base) return 0;
      if (last.value_type === 'price') {
        return last.value < base ? Math.round(((base - last.value) / base) * 100) : 0;
      }
      if (last.value_type === 'fixed_amount') {
        return Math.round(((last.value || 0) / base) * 100);
      }
      return 0;
    },

    money(cents) {
      if (typeof cents !== 'number') return '';
      return `$${(cents / 100).toFixed(2)}`;
    },

    /** Values the copy tokens in snippets/ctr-skio-copy.liquid resolve to. */
    copyValues(variant, plan) {
      return {
        discount: `${this.discountPercentage(variant, plan)}%`,
        renewal_discount: `${this.renewalPercentage(variant, plan)}%`,
        price: this.money(this.unitSubscribePrice(variant, plan)),
        compare_price: this.money(this.basePrice(variant)),
      };
    },

    /**
     * Re-resolve copy tokens after the shopper changes variant or plan.
     *
     * Liquid already rendered these correctly for first paint and left the token
     * template in data-skio-copy, so this only has to run again when the numbers
     * move. Copy without tokens has no such attribute and is never touched.
     */
    applyCopyTokens(root, values) {
      if (!root || !values) return;

      root.querySelectorAll('[data-skio-copy]').forEach((el) => {
        const template = el.dataset.skioCopy;
        if (!template) return;

        const resolved = template.replace(/\[skio_([a-z_]+)\]/g, (token, key) => {
          if (Object.prototype.hasOwnProperty.call(values, key)) return values[key];
          console.warn(`ctr-skio-plan-utils: unknown copy token ${token}`);
          return '';
        });

        // Writing unconditionally would fight Alpine on every re-render
        if (el.innerHTML !== resolved) el.innerHTML = resolved;
      });
    },
  };
})();
